import React from 'react';
import { Alert, Button, Col, Form, FormField, FormRow, formWithValidation, Right, Row } from '@edgebox/react-components';
import { type IOptionalSerializedReference, InternalId } from '@edgebox/data-definition-kit';
import {
  AccessScope,
  AiModelCapability,
  AiModelModality,
  AiModelOptimizationType,
  AiModelStatus,
  AiModelUseCase,
  type ISyncCoreAiModelDTO,
  type ISyncCoreSerializedAiModelProperties,
} from '@edgebox/sync-core-data-definitions';
import type { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { ClientAiModelDraft, ClientAiModelEntity } from '@edgebox/sync-core-rest-client';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { enumValues, normalizeFilterId } from '../aiHelpers';
import { AiProviderSelector } from './AiProviderSelector';

const ModelDraftForm = formWithValidation<ISyncCoreSerializedAiModelProperties, ClientAiModelDraft>(
  (values) => plainToInstance(ClientAiModelDraft, values),
  (entity) => instanceToPlain(entity) as ISyncCoreSerializedAiModelProperties
);

const ModelEntityForm = formWithValidation<ISyncCoreAiModelDTO, ClientAiModelEntity>(
  (values) => plainToInstance(ClientAiModelEntity, values),
  (entity) => instanceToPlain(entity) as ISyncCoreAiModelDTO
);

function optionalSerializedReference(id?: InternalId): IOptionalSerializedReference | undefined {
  if (!id) return undefined;
  return { _: 1, id };
}

function toggleArrayValue<T extends string>(current: T[], value: T, nextChecked: boolean): T[] {
  return nextChecked ? (current.includes(value) ? current : [...current, value]) : current.filter((c) => c !== value);
}

function getSubmitErrorMessage(error: unknown): string {
  if (!error) return '';

  const responseData = (error as any)?.response?.data;
  const payload = responseData ?? error;

  if (payload && typeof payload === 'object') {
    const message = (payload as any).message;
    const statusCode = (payload as any).statusCode;
    const errorLabel = (payload as any).error;

    if (Array.isArray(message)) {
      const lines = message
        .map((m) => {
          const property = (m as any)?.property;
          const constraints = (m as any)?.constraints;
          const constraintText = constraints && typeof constraints === 'object' ? Object.values(constraints).join(' ') : undefined;
          if (property && constraintText) return `${property}: ${constraintText}`;
          if (property) return String(property);
          return typeof m === 'string' ? m : JSON.stringify(m);
        })
        .filter(Boolean);

      const header = [statusCode ? String(statusCode) : undefined, errorLabel ? String(errorLabel) : undefined].filter(Boolean).join(' ');
      return `${header || 'Request failed'}\n${lines.join('\n')}`;
    }

    if (typeof message === 'string' && message.length) return message;
  }

  return error instanceof Error ? error.message : String(error);
}

function ensureAiModelDefaults(model: ClientAiModelDraft | ClientAiModelEntity) {
  if (!model.optimizationTypes?.length) model.optimizationTypes = [AiModelOptimizationType.Pretrained];
}

function ArrayCheckboxes<T extends string>({
  title,
  values,
  options,
  onChange,
}: {
  title: string;
  values: T[];
  options: T[];
  onChange: (next: T[]) => void;
}) {
  return (
    <div className="mb-3">
      <div className="fw-bold mb-1">{title}</div>
      {options.map((opt) => (
        <Form.Check
          key={opt}
          type="checkbox"
          label={opt}
          checked={values.includes(opt)}
          onChange={(e) => onChange(toggleArrayValue(values, opt, e.target.checked))}
        />
      ))}
    </div>
  );
}

export function AiModelDraftEditor({
  api,
  initialValues,
  submitLabel,
  onSubmit,
}: {
  api: ISyncCoreApi;
  initialValues: ClientAiModelDraft;
  submitLabel: string;
  onSubmit: (draft: ClientAiModelDraft) => Promise<void>;
}) {
  const [saving, setSaving] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<unknown>(undefined);

  return (
    <ModelDraftForm
      initialValues={initialValues}
      onSubmit={async (draft) => {
        setSaving(true);
        setSubmitError(undefined);
        try {
          ensureAiModelDefaults(draft);
          await onSubmit(draft);
        } catch (e) {
          setSubmitError(e);
        } finally {
          setSaving(false);
        }
      }}
    >
      {({ values, setValue }) => {
        const customerId = values.customer?.id ?? '';
        const projectId = values.project?.id ?? '';

        return (
          <>
            {submitError ? (
              <Alert variant="danger" className="mb-3">
                <div className="fw-bold">Save failed</div>
                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {getSubmitErrorMessage(submitError)}
                </pre>
              </Alert>
            ) : null}
            <Row>
              <Col md={6}>
                <FormRow name={'name'} label={'Name'} labelPlacement={'above'} required>
                  <FormField name={'name'} type={'text'} noRow />
                </FormRow>
              </Col>
              <Col md={3}>
                <FormRow name={'status'} label={'Status'} labelPlacement={'above'} required>
                  <Form.Select value={values.status} onChange={(e) => setValue('status', e.target.value as AiModelStatus)}>
                    {enumValues(AiModelStatus).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>
              <Col md={3}>
                <FormRow name={'accessScope'} label={'Access scope'} labelPlacement={'above'} required>
                  <Form.Select value={values.accessScope} onChange={(e) => setValue('accessScope', e.target.value as AccessScope)}>
                    {enumValues(AccessScope).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormRow name={'modelKey'} label={'Model key'} labelPlacement={'above'} required>
                  <FormField name={'modelKey'} type={'text'} noRow />
                </FormRow>
              </Col>
              <Col md={8}>
                <FormRow name={'frozen'} label={'Frozen'} labelPlacement={'above'}>
                  <Form.Check type="checkbox" label="" checked={values.frozen} onChange={(e) => setValue('frozen', e.target.checked)} />
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormRow name={'versionLabel'} label={'Version label'} labelPlacement={'above'}>
                  <FormField name={'versionLabel'} type={'text'} noRow />
                </FormRow>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <FormRow name={'provider'} label={'Provider'} labelPlacement={'above'} required>
                  <AiProviderSelector
                    name="provider.id"
                    api={api}
                    value={values.provider?.id}
                    onChange={(nextId) => {
                      if (!nextId) return;
                      setValue('provider', { _: 1, id: nextId });
                    }}
                    disabled={saving}
                  />
                </FormRow>
              </Col>
            </Row>

            <ArrayCheckboxes<AiModelUseCase>
              title="Use cases"
              values={values.useCases ?? []}
              options={enumValues(AiModelUseCase)}
              onChange={(next) => setValue('useCases', next)}
            />

            <ArrayCheckboxes<AiModelOptimizationType>
              title="Optimization types"
              values={values.optimizationTypes ?? []}
              options={enumValues(AiModelOptimizationType)}
              onChange={(next) => setValue('optimizationTypes', next)}
            />

            <ArrayCheckboxes<AiModelCapability>
              title="Capabilities"
              values={values.capabilities ?? []}
              options={enumValues(AiModelCapability)}
              onChange={(next) => setValue('capabilities', next)}
            />

            <ArrayCheckboxes<AiModelModality>
              title="Input modalities"
              values={values.inputModalities ?? []}
              options={enumValues(AiModelModality)}
              onChange={(next) => setValue('inputModalities', next)}
            />

            <ArrayCheckboxes<AiModelModality>
              title="Output modalities"
              values={values.outputModalities ?? []}
              options={enumValues(AiModelModality)}
              onChange={(next) => setValue('outputModalities', next)}
            />

            <Row>
              <Col md={4}>
                <FormRow name={'maxInputTokens'} label={'Max input tokens'} labelPlacement={'above'}>
                  <FormField name={'maxInputTokens'} type={'number'} noRow />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'maxOutputTokens'} label={'Max output tokens'} labelPlacement={'above'}>
                  <FormField name={'maxOutputTokens'} type={'number'} noRow />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'contextWindowTokens'} label={'Context window tokens'} labelPlacement={'above'}>
                  <FormField name={'contextWindowTokens'} type={'number'} noRow />
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormRow name={'costPerInputToken'} label={'Cost / 1M input tokens'} labelPlacement={'above'}>
                  <Form.Control
                    type="number"
                    value={Number.isFinite(values.costPerInputToken) ? values.costPerInputToken * 1_000_000 : 0}
                    onChange={(e) => {
                      const next = parseFloat(e.target.value);
                      setValue('costPerInputToken', Number.isFinite(next) ? next / 1_000_000 : 0);
                    }}
                  />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'costPerOutputToken'} label={'Cost / 1M output tokens'} labelPlacement={'above'}>
                  <Form.Control
                    type="number"
                    value={Number.isFinite(values.costPerOutputToken) ? values.costPerOutputToken * 1_000_000 : 0}
                    onChange={(e) => {
                      const next = parseFloat(e.target.value);
                      setValue('costPerOutputToken', Number.isFinite(next) ? next / 1_000_000 : 0);
                    }}
                  />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'costPerRequest'} label={'Cost / 1K requests'} labelPlacement={'above'}>
                  <Form.Control
                    type="number"
                    value={Number.isFinite(values.costPerRequest) ? values.costPerRequest * 1_000 : 0}
                    onChange={(e) => {
                      const next = parseFloat(e.target.value);
                      setValue('costPerRequest', Number.isFinite(next) ? next / 1_000 : 0);
                    }}
                  />
                </FormRow>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label className="fw-bold">Customer ID</Form.Label>
                <Form.Control
                  value={customerId}
                  onChange={(e) => {
                    const id = normalizeFilterId(e.target.value);
                    setValue('customer', optionalSerializedReference(id));
                  }}
                  placeholder="Optional"
                />
              </Col>
              <Col md={6}>
                <Form.Label className="fw-bold">Project ID</Form.Label>
                <Form.Control
                  value={projectId}
                  onChange={(e) => {
                    const id = normalizeFilterId(e.target.value);
                    setValue('project', optionalSerializedReference(id));
                  }}
                  placeholder="Optional"
                />
              </Col>
            </Row>

            <Right className="pe-1">
              <Button variant="primary" type="submit" disabled={saving}>
                {submitLabel}
              </Button>
            </Right>
          </>
        );
      }}
    </ModelDraftForm>
  );
}

export function AiModelEntityEditor({
  api,
  initialValues,
  submitLabel,
  onSubmit,
}: {
  api: ISyncCoreApi;
  initialValues: ClientAiModelEntity;
  submitLabel: string;
  onSubmit: (entity: ClientAiModelEntity) => Promise<void>;
}) {
  const [saving, setSaving] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<unknown>(undefined);

  return (
    <ModelEntityForm
      initialValues={initialValues}
      onSubmit={async (entity) => {
        setSaving(true);
        setSubmitError(undefined);
        try {
          ensureAiModelDefaults(entity);
          await onSubmit(entity);
        } catch (e) {
          setSubmitError(e);
        } finally {
          setSaving(false);
        }
      }}
    >
      {({ values, setValue }) => {
        const customerId = values.customer?.id ?? '';
        const projectId = values.project?.id ?? '';

        return (
          <>
            {submitError ? (
              <Alert variant="danger" className="mb-3">
                <div className="fw-bold">Save failed</div>
                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {getSubmitErrorMessage(submitError)}
                </pre>
              </Alert>
            ) : null}
            <Row>
              <Col md={6}>
                <FormRow name={'name'} label={'Name'} labelPlacement={'above'} required>
                  <FormField name={'name'} type={'text'} noRow />
                </FormRow>
              </Col>
              <Col md={3}>
                <FormRow name={'status'} label={'Status'} labelPlacement={'above'} required>
                  <Form.Select value={values.status} onChange={(e) => setValue('status', e.target.value as AiModelStatus)}>
                    {enumValues(AiModelStatus).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>
              <Col md={3}>
                <FormRow name={'accessScope'} label={'Access scope'} labelPlacement={'above'} required>
                  <Form.Select value={values.accessScope} onChange={(e) => setValue('accessScope', e.target.value as AccessScope)}>
                    {enumValues(AccessScope).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormRow name={'modelKey'} label={'Model key'} labelPlacement={'above'} required>
                  <FormField name={'modelKey'} type={'text'} noRow />
                </FormRow>
              </Col>
              <Col md={8}>
                <FormRow name={'frozen'} label={'Frozen'} labelPlacement={'above'}>
                  <Form.Check type="checkbox" label="" checked={values.frozen} onChange={(e) => setValue('frozen', e.target.checked)} />
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormRow name={'versionLabel'} label={'Version label'} labelPlacement={'above'}>
                  <FormField name={'versionLabel'} type={'text'} noRow />
                </FormRow>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <FormRow name={'provider'} label={'Provider'} labelPlacement={'above'} required>
                  <AiProviderSelector
                    name="provider.id"
                    api={api}
                    value={values.provider?.id}
                    onChange={(nextId) => {
                      if (!nextId) return;
                      setValue('provider', { _: 1, id: nextId });
                    }}
                    disabled={saving}
                  />
                </FormRow>
              </Col>
            </Row>

            <ArrayCheckboxes<AiModelUseCase>
              title="Use cases"
              values={values.useCases ?? []}
              options={enumValues(AiModelUseCase)}
              onChange={(next) => setValue('useCases', next)}
            />

            <ArrayCheckboxes<AiModelOptimizationType>
              title="Optimization types"
              values={values.optimizationTypes ?? []}
              options={enumValues(AiModelOptimizationType)}
              onChange={(next) => setValue('optimizationTypes', next)}
            />

            <ArrayCheckboxes<AiModelCapability>
              title="Capabilities"
              values={values.capabilities ?? []}
              options={enumValues(AiModelCapability)}
              onChange={(next) => setValue('capabilities', next)}
            />

            <ArrayCheckboxes<AiModelModality>
              title="Input modalities"
              values={values.inputModalities ?? []}
              options={enumValues(AiModelModality)}
              onChange={(next) => setValue('inputModalities', next)}
            />

            <ArrayCheckboxes<AiModelModality>
              title="Output modalities"
              values={values.outputModalities ?? []}
              options={enumValues(AiModelModality)}
              onChange={(next) => setValue('outputModalities', next)}
            />

            <Row>
              <Col md={4}>
                <FormRow name={'maxInputTokens'} label={'Max input tokens'} labelPlacement={'above'}>
                  <FormField name={'maxInputTokens'} type={'number'} noRow />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'maxOutputTokens'} label={'Max output tokens'} labelPlacement={'above'}>
                  <FormField name={'maxOutputTokens'} type={'number'} noRow />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'contextWindowTokens'} label={'Context window tokens'} labelPlacement={'above'}>
                  <FormField name={'contextWindowTokens'} type={'number'} noRow />
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormRow name={'costPerInputToken'} label={'Cost / 1M input tokens'} labelPlacement={'above'}>
                  <Form.Control
                    type="number"
                    value={Number.isFinite(values.costPerInputToken) ? values.costPerInputToken * 1_000_000 : 0}
                    onChange={(e) => {
                      const next = parseFloat(e.target.value);
                      setValue('costPerInputToken', Number.isFinite(next) ? next / 1_000_000 : 0);
                    }}
                  />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'costPerOutputToken'} label={'Cost / 1M output tokens'} labelPlacement={'above'}>
                  <Form.Control
                    type="number"
                    value={Number.isFinite(values.costPerOutputToken) ? values.costPerOutputToken * 1_000_000 : 0}
                    onChange={(e) => {
                      const next = parseFloat(e.target.value);
                      setValue('costPerOutputToken', Number.isFinite(next) ? next / 1_000_000 : 0);
                    }}
                  />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'costPerRequest'} label={'Cost / 1K requests'} labelPlacement={'above'}>
                  <Form.Control
                    type="number"
                    value={Number.isFinite(values.costPerRequest) ? values.costPerRequest * 1_000 : 0}
                    onChange={(e) => {
                      const next = parseFloat(e.target.value);
                      setValue('costPerRequest', Number.isFinite(next) ? next / 1_000 : 0);
                    }}
                  />
                </FormRow>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label className="fw-bold">Customer ID</Form.Label>
                <Form.Control
                  value={customerId}
                  onChange={(e) => {
                    const id = normalizeFilterId(e.target.value);
                    setValue('customer', optionalSerializedReference(id));
                  }}
                  placeholder="Optional"
                />
              </Col>
              <Col md={6}>
                <Form.Label className="fw-bold">Project ID</Form.Label>
                <Form.Control
                  value={projectId}
                  onChange={(e) => {
                    const id = normalizeFilterId(e.target.value);
                    setValue('project', optionalSerializedReference(id));
                  }}
                  placeholder="Optional"
                />
              </Col>
            </Row>

            <Right className="pe-1">
              <Button variant="primary" type="submit" disabled={saving}>
                {submitLabel}
              </Button>
            </Right>
          </>
        );
      }}
    </ModelEntityForm>
  );
}
